// @flow
import {allFlattenRoutes} from "../routes/index";
import { generatePath } from "react-router-dom";
import type {RouteType} from "../flow/RouteType";
import qs from 'query-string';
import { loadStripe } from "@stripe/stripe-js";
/// XXX: Please make sure you sync the date-fns locales with the i18n supported languages
import {
    enUS as dateEn,
    cs as dateCs,
    da as dateDa,
    de as dateDe,
    el as dateEl,
    es as dateEs,
    fi as dateFi,
    fr as dateFr,
    hi as dateHi,
    hr as dateHr,
    hu as dateHu,
    id as dateId,
    it as dateIt,
    ja as dateJa,
    ko as dateKo,
    nl as dateNl,
    nb as dateNo,
    pl as datePl,
    ptBR as datePt,
    ro as dateRo,
    ru as dateRu,
    sv as dateSv,
    th as dateTh,
    tr as dateTr,
    uk as dateUk,
    vi as dateVi,
    zhCN as dateZhCn,
} from "date-fns/locale";
import { format } from "date-fns";
import { sanitizeUrl } from "@braintree/sanitize-url";
import sanitizeHTML from "sanitize-html";

/* istanbul ignore next -- cannot test: env var checked at module load time, cannot be mocked before Jest loads this module */
if (!import.meta.env.VITE_STRIPE_KEY) {
    throw new Error('Stripe key is required!');
}

export const dateLocaleSelector = {
    en: dateEn,
    de: dateDe,
    es: dateEs,
    fr: dateFr,
    it: dateIt,
    ja: dateJa,
    nl: dateNl,
    ru: dateRu,
    vi: dateVi,
    cs: dateCs,
    da: dateDa,
    el: dateEl,
    fi: dateFi,
    hi: dateHi,
    hr: dateHr,
    hu: dateHu,
    id: dateId,
    ko: dateKo,
    no: dateNo,
    pl: datePl,
    pt: datePt,
    ro: dateRo,
    sv: dateSv,
    th: dateTh,
    tr: dateTr,
    uk: dateUk,
    'zh-CN': dateZhCn,
};

/**
 * The global shared Stripe object.
 * 
 * @type {Promise<import('@stripe/stripe-js').Stripe | null>}
 */
const globalStripe = loadStripe(import.meta.env.VITE_STRIPE_KEY);

/**
*
 * Returns a promise resolving to the Stripe object.
 * @returns {Promise<import('@stripe/stripe-js').Stripe | null>}
 */
/* istanbul ignore next -- cannot test: Stripe key env var checked at module load, globalStripe is always truthy Promise */
export async function getStripe() {
    // Check by .then function signature for a "PromiseLike"
    if (typeof globalStripe.then === 'function') {
        // load stripe
        return await globalStripe;
    }

    // Already loaded
    return globalStripe;
}

export const capitalize = (str: string) => typeof str === 'string' ? str.charAt(0).toUpperCase() + str.slice(1) : str;

export const formatBytes = (input, digits) => {
    if (0 === input) return "0 Bytes";

    const divisor = 1024;
    const frac = digits || 2;
    const byteSuffix = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
    const exp = Math.floor(Math.log(input) / Math.log(divisor));

    return parseFloat((input / Math.pow(divisor, exp)).toFixed(frac)) + " " + byteSuffix[exp]
};

export const nFormatter = (num, digits, stopAt = null) => {
    const si = [
        { value: 1, symbol: "" },
        { value: 1E3, symbol: "K" },
        { value: 1E6, symbol: "M" },
        { value: 1E9, symbol: "G" },
        { value: 1E12, symbol: "T" },
        { value: 1E15, symbol: "P" },
        { value: 1E18, symbol: "E" }
    ];
    const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
    let i;
    for (i = si.length - 1; i > 0; i--) {
        if (num >= si[i].value) {
            break;
        }
    }

    if (stopAt) {
        const stopIndex = si.findIndex(s => s.symbol === stopAt);
        if (stopIndex !== -1 && i > stopIndex) {
            i = stopIndex;
        }
    }

    return (num / si[i].value).toFixed(digits).replace(rx, "$1") + si[i].symbol;
}

export const route = (name, params, query) => {
    const matchRoute = allFlattenRoutes.find((r: RouteType) => r.routeName === name);
    const url = generatePath(matchRoute.path, params);
    return query ? `${url}?${qs.stringify(query)}` : url;                                                                          
};

export const getDateLocaleData = (lang: string) => {
    const locale = shortLangCode(lang);
    return dateLocaleSelector[lang] || dateLocaleSelector[locale] || dateEn;
};

export const formatLocalizedDate = (date: string | Date, lang: string) => {
    const selectedLocale = getDateLocaleData(lang);
    return format(new Date(date), 'PPP', {locale: selectedLocale})
}

export const shortLangCode = (lang: string) => typeof lang === 'string' ? (lang.toLowerCase().split('-'))[0] : lang;

/**
 * Format revenue number according to Stripe currency formatting
 *
 * Reference: https://docs.stripe.com/currencies#presentment-currencies
 *
 * @param {number} amount
 * @param {string} currency
 * @return {number}
 */
export function formatStripeAmount(amount, currency) {
    if (!amount || !currency) {
        return amount;
    }

    // https://docs.stripe.com/currencies#zero-decimal
    const zeroDecimal = [
        'BIF',
        'CLP',
        'DJF',
        'GNF',
        'JPY',
        'KMF',
        'KRW',
        'MGA',
        'PYG',
        'RWF',
        'UGX',
        'VND',
        'VUV',
        'XAF',
        'XOF',
        'XPF',
    ];

    if (zeroDecimal.includes(currency.toUpperCase())) {
        return amount;
    }

    return amount / 100;
}

/**
 * Sanitizes a URL and returns the sanitized version,
 * or null if the URL is invalid.
 *
 * @param {string} url
 * @returns {URL | null}
 */
export const urlSanitizer = (url) => {
    /** @type {URL} */
    let urlVerify;

    try {
        urlVerify = new URL(url);
    } catch {
        return null;
    }

    const preSanitize = sanitizeUrl(urlVerify.href);
    if (preSanitize === 'about:blank') {
        return null;
    }

    const parsedUrl = new URL(preSanitize);

    // Go through all query param
    parsedUrl.searchParams.forEach((value, key) => {
        // Sanitize each to remove unsafe HTML
        parsedUrl.searchParams.set(key, sanitizeHTML(value));
    });

    return parsedUrl;
}
